import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../../pages/auth/services/auth.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const _annonymousUrls = [
    '/api/auth/auth0-login',
    '/api/auth/token/refresh/',
    'https://www.arcgis.com/sharing/rest',
    'https://arcgis.accdata.org/server/sharing',
    'https://geocode.arcgis.com',
    '.blob.core.windows.net/',
    'https://acc-dh.maps.arcgis.com',
    '/api/cdfi-outreach/form/',
    '/api/cdfi/public/',
    '/api/container/sas-url',
    '/api/email/send-otp',
    '/api/connections/self-signup',
  ];

  // Check for public links redirect URLs
  const publicLinkRedirectPattern =
    /\/api\/admin\/public-links\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/redirect\?access_type=(link|qr)/i;

  const shouldSkip =
    req.headers.has('x-api-key') ||
    req.headers.has('X-Api-Key') ||
    _annonymousUrls.some((url) => req.url.includes(url)) ||
    publicLinkRedirectPattern.test(req.url);

  if (shouldSkip) {
    return next(req);
  }

  const token = authService.getStoredToken();
  if (token) {
    req = addToken(req, token);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        !req.url.includes('/api/auth/token/logout/')
      ) {
        return handle401Error(req, next, authService);
      }
      return throwError(() => error);
    }),
  );
};

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
  string | null
>(null);

const handle401Error = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<unknown>> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((token: string) => {
        isRefreshing = false;
        refreshTokenSubject.next(token);
        return next(addToken(request, token));
      }),
      catchError((err) => {
        isRefreshing = false;
        authService.logout().subscribe();
        return throwError(() => err);
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((token) => {
        return next(addToken(request, token));
      }),
    );
  }
};

const addToken = (request: HttpRequest<unknown>, token: string) => {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};
